/* eslint semi:0 */
import { namespacedConsts } from '../../utils/object'
import { randId } from '../../utils/string'
import BaseVm from './BaseVm'
import userStore from '../stores/userStore'
import { queryString } from '../../utils/window'
import { TYPES, videoStartedPlaying } from '../actions/brightcovePlayerActions'
import { getVideoAdTargeting } from './ads/adTargeting'
import { ads as pageAdsConf } from '../../application/pageDataObject'
import CompoundBrightcovePlayer, {
  brightcovePlayerProvider,
} from '../../providers/brightcove/brightcovePlayerProvider'
import { videoAdStarted } from '../actions/adActions'

const CLASS_CONSTANTS = namespacedConsts({
  USER_ROLES: {
    ADMIN: true,
    OTHER: true,
  },
  STATUSES: {
    TEMPLATE_READY: true,
  },
})

const TRUE = 'true'

const DEFAULT_PARAMS = {
  bgcolor: '#000000',
  width: '100%',
  height: '100%',
  isVid: TRUE,
  isUI: TRUE,
  dynamicStreaming: TRUE,
  includeAPI: TRUE,
  wmode: 'transparent',
  allowScriptAccess: 'always',
  allowFullScreen: TRUE,
  // htmlFallback: TRUE,
}

const { noPlay = false, adsVideoAdUnitPath, adsVideoDebug = false } = queryString

export default class BrightcoveBasePlayerVm extends BaseVm {
  constructor(...args) {
    super(...args)
    let { rootNode } = this.options
    this.merge({
      rootNode,
      hasReadyHandlerFired: false,
    }).generateElementId()
    this.parseInlineData().loadExternal()
  }

  onChange(action) {
    if (this.isRelevantTemplateReadyAction(action)) {
      this.onTemplateReady(action.player)
    }
    return super.onChange(action)
  }

  isRelevantTemplateReadyAction(action) {
    return !!(
      action &&
      action.type === TYPES.TEMPLATE_READY &&
      action.player.playerId === this.elementId
    )
  }

  onTemplateReady(player) {
    // this comment is taken from Brightcove's support example:
    // with ads enabled, onTemplateReady fires twice
    if (this.hasReadyHandlerFired) {
      return this
    }
    this.merge({
      player,
      hasReadyHandlerFired: true,
      status: CLASS_CONSTANTS.STATUSES.TEMPLATE_READY,
    })
    player.getAdModulePolicy(this.handleAdPolicy.bind(this))
    let videoId = this.videoPlayerConfigurationParams.videoId
    player.adModule.addEventListener(
      window.brightcove.api.events.AdEvent.START,
      videoAdStarted.part({
        ad_unit: this.adUnitPath,
        player_ad_keys: this.adPolicy.playerAdKeys,
        video_id: videoId,
      })
    )
    player.addCompoundEventListener(
      window.brightcove.api.events.MediaEvent.PLAY,
      videoStartedPlaying.part({ video_id: videoId })
    )
    return this
  }

  initPlayback() {
    if (this.shouldAutoplay) {
      this.play()
    }
    return this
  }

  play() {
    if (!noPlay) {
      this.applyUserVolumeIfNeeded().player.play()
    }
    return this
  }

  get adFormat() {
    throw new Error(this.constructor.MUST_OVERRIDE_METHOD_ERROR_MESSAGE)
  }

  get adUnitPath() {
    if (adsVideoAdUnitPath) {
      return adsVideoAdUnitPath
    }
    let { hierarchyLevels } = pageAdsConf
    hierarchyLevels.l4 = this.adFormat
    return hierarchyLevels.toString()
  }

  get adPolicy() {
    return {
      playAdOnLoad: false, // should ads be displayed as soon as the player is loaded?
      prerollAds: true, // when false, played all ads at end (post-roll)
      adServerURL: this.adServerUrl,
      adPlayCap: 1, // number of ads to display
      playerAdKeys: getVideoAdTargeting(this.ownCustomCriteria),
    }
  }

  handleAdPolicy(videocloudAdPolicy) {
    let { adPolicy } = this
    adsVideoDebug && this.info('handleAdPolicy', adPolicy)
    if (adPolicy) {
      this.player.setAdModulePolicy(adPolicy, this.onAdPolicySet.bind(this))
    } else {
      this.onAdPolicySet(videocloudAdPolicy)
    }
    return this
  }

  onAdPolicySet(adPolicy) {
    adsVideoDebug && this.adPolicy && this.info('after:', adPolicy.adServerURL)
    this.initPlayback()
  }

  get isAutoplayAllowed() {
    return false
  }

  get ownCustomCriteria() {
    let { adFormat: format, playerId, isAutoplayAllowed } = this
    return new Map([
      ['format', format],
      ['playerId', playerId],
      ['isAutoplayAllowed', isAutoplayAllowed],
    ])
  }

  get adServerUrl() {
    return `http://pubads.g.doubleclick.net/gampad/ads?sz=640x480&iu=${this
      .adUnitPath}&ciu_szs&impl=s&gdfp_req=1&env=vp&output=xml_vast2&unviewed_position_start=1&cmsid=612`
  }

  get isTemplateReady() {
    return this.status === CLASS_CONSTANTS.STATUSES.TEMPLATE_READY
  }

  set player(newPlayer) {
    this._player = newPlayer
  }
  get player() {
    return this._player || (this._player = CompoundBrightcovePlayer.create(this.elementId))
  }

  set status(newStatus) {
    this._status = newStatus
  }
  get status() {
    return this._status
  }

  static get STATUSES() {
    return CLASS_CONSTANTS.STATUSES
  }

  generateElementId() {
    this.elementId = `${this.playerIdPrefix}_${randId()}`
  }

  get playerIdPrefix() {
    return `_brightcove_${this.playerName}_player`
  }

  get playerName() {
    throw new Error('Must implement get playerName() on subclass')
  }

  parseInlineData() {
    let videoId = this.getInlineAttribute('video-id')
    let playerId = this.getInlineAttribute('player-id')
    return this.merge({
      videoId,
      playerId,
      inlineData: {
        videoId,
        playerId,
        playerKey: this.getInlineAttribute('player-key'),
      },
    })
  }

  loadExternal() {
    brightcovePlayerProvider.load()
  }

  set elementId(newId) {
    this._elementId = newId
  }
  get elementId() {
    return this._elementId
  }

  applyUserVolumeIfNeeded() {
    if (this.shouldApplyVolume) {
      this.applyUserVolume()
    }
    return this
  }

  get shouldApplyVolume() {
    return this.shouldAutoplay
  }

  get shouldAutoplay() {
    throw new Error('Must implement get shouldAutoplay() on subclass')
  }

  applyUserVolume() {
    this.player.setVolume(this.userVolume)
  }

  get userVolume() {
    return this.usersVolumes[this.userRole]
  }

  get usersVolumes() {
    throw new Error('Must implement get userVolumes() on subclass')
  }

  get userRole() {
    return userStore.state.admin
      ? CLASS_CONSTANTS.USER_ROLES.ADMIN
      : CLASS_CONSTANTS.USER_ROLES.OTHER
  }

  getInlineAttribute(attr) {
    return this.rootNode.getAttribute(`data-${attr}`)
  }

  get htmlParams() {
    return Object.assign(DEFAULT_PARAMS, this.videoPlayerConfigurationParams, this.handlersParams)
  }

  get videoPlayerConfigurationParams() {
    return {
      videoId: this.inlineData.videoId,
      playerKey: this.inlineData.playerKey,
      playerId: this.inlineData.playerId,
      autoStart: this.autoStart,
    }
  }

  get autoStart() {
    return this.shouldAutoplay
  }

  get isExternalLoaded() {
    return this.store.isExternalLoaded
  }

  get handlersParams() {
    return {
      templateReadyHandler: brightcovePlayerProvider.handlers.templateReady.globalInvocationName(
        this.elementId
      ),
    }
  }
}



// WEBPACK FOOTER //
// ./provolution/app/views/provolution/jsCore/organizedByType/vms/brightcoveBasePlayerVm.js